import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight } from 'lucide-react';

const Zoho = () => {
  const zohoProducts = [
    {
      name: 'Zoho CRM',
      description: 'Complete CRM oplossing voor sales en marketing',
      link: '/zoho-crm',
    },
    {
      name: 'Zoho Desk',
      description: 'Helpdesk en klantenservice software',
      link: '/zoho-desk',
    },
    {
      name: 'Zoho Books',
      description: 'Online boekhouden en facturatie',
      link: '/zoho-books',
    },
    {
      name: 'Zoho Projects',
      description: 'Projectmanagement voor teams',
      link: '/zoho-projects',
    },
    {
      name: 'Zoho People',
      description: 'HR management systeem',
      link: '/zoho-people',
    },
    {
      name: 'Zoho Vault',
      description: 'Wachtwoordbeheer voor bedrijven',
      link: '/zoho-vault',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Zoho: Complete Business Software Suite
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Zoho biedt een compleet ecosysteem van business applicaties. Van CRM tot boekhouding, 
              van projectmanagement tot HR - alles naadloos geïntegreerd.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  Plan een Demo
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat is Zoho?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Zoho Corporation is een van de grootste en meest succesvolle SaaS-bedrijven ter wereld, met meer dan 
                90 miljoen gebruikers wereldwijd. Zoho biedt een uitgebreid pakket aan cloud-gebaseerde business 
                applicaties die bedrijven helpen om te groeien en efficiënter te werken.
              </p>
              <p>
                Het grote voordeel van Zoho is dat alle applicaties naadloos met elkaar samenwerken. Data uit Zoho CRM 
                kan direct gebruikt worden in Zoho Books voor facturatie, projecten in Zoho Projects kunnen gekoppeld 
                worden aan klanten in het CRM, en HR data uit Zoho People kan gebruikt worden voor projectplanning.
              </p>
              <p>
                Als officiële Zoho Partner helpt Guijs Consultancy Nederlandse bedrijven om het maximale uit het Zoho 
                ecosysteem te halen. Wij adviseren welke Zoho producten het beste passen bij uw organisatie en zorgen 
                voor een professionele implementatie.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Populaire Zoho Producten
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ontdek de meest gebruikte Zoho applicaties die wij implementeren.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {zohoProducts.map((product, index) => (
              <Card key={`item-${index}`} className="border-2 border-gray-200 hover:border-blue-500 transition-all duration-300 hover:shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{product.name}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <Link 
                    to={product.link}
                    className="text-blue-700 hover:text-blue-800 font-semibold inline-flex items-center group"
                  >
                    Meer informatie
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Voordelen van Zoho voor Uw Bedrijf
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
              {[
                'Alles geïntegreerd in één ecosysteem',
                'Betaalbaar - ook voor MKB bedrijven',
                'Schaalbaar van startup tot enterprise',
                'Nederlandse ondersteuning via partners',
                'Voldoet aan Europese privacy wetgeving (AVG)',
                'Regelmatige updates met nieuwe features',
              ].map((benefit, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection 
        title="Klaar om te Starten met Zoho?"
        subtitle="Ontdek welke Zoho producten perfect passen bij uw bedrijf."
      />
    </div>
  );
};

export default Zoho;
